import { t } from "@lingui/macro"
import { ApiDynamicFilterType } from "@src/api/filters"
import {
  IterationType,
  SurveyFilters,
  SurveyResponse,
  SurveyResponsesFilters,
  SurveyStatus,
  SurveyType,
} from "@src/api/survey"
import { ReportType } from "@src/components/organisms/Answers/Reporting/answerLabel"
import { QueueStatusTone } from "@src/components/organisms/QueueSidebar"
import { getSurveyIterationType } from "./utils"

export type ResponseStatus = "submitted" | "draft" | "missing"

export type ViewOption = { id: number; label: string; status?: ResponseStatus }

export type ResponsesViewMode =
  | "response"
  | "responderSummary"
  | "iterationSummary"
  | "allResponses"

const isNonRecurrent = (iterationType?: IterationType) =>
  iterationType === IterationType.NonRecurrent

export const getResponsesViewMode = ({
  responderId,
  iteration,
  iterationType,
}: {
  responderId?: number
  iteration?: number
  iterationType?: IterationType
}): ResponsesViewMode => {
  const hasResponder = responderId !== undefined

  if (isNonRecurrent(iterationType)) {
    return hasResponder ? "response" : "allResponses"
  }

  const hasIteration = iteration !== undefined

  if (hasResponder && hasIteration) {
    return "response"
  }
  if (hasResponder) {
    return "responderSummary"
  }
  if (hasIteration) {
    return "iterationSummary"
  }

  return "allResponses"
}

export const getResponderId = (
  response: SurveyResponse,
  surveyType?: SurveyType,
) => {
  if (surveyType === SurveyType.Startup) {
    return response.projectId ?? undefined
  }

  return response.userId
}

const getResponseStatus = (responses: SurveyResponse[]): ResponseStatus => {
  if (responses.length === 0) {
    return "missing"
  }

  const hasSubmitted = responses.some(
    (response) => response.status === SurveyStatus.Submitted,
  )

  return hasSubmitted ? "submitted" : "draft"
}

const preferSubmitted = (responses: SurveyResponse[]) =>
  responses.find((response) => response.status === SurveyStatus.Submitted) ??
  responses[0]

const withStatus = (
  option: ViewOption,
  matchingResponses?: SurveyResponse[],
): ViewOption => {
  if (!matchingResponses) {
    return option
  }

  return { ...option, status: getResponseStatus(matchingResponses) }
}

const getResponderBaseOptions = (
  surveyType?: SurveyType,
  filters?: SurveyFilters,
): ViewOption[] => {
  if (surveyType === SurveyType.Startup) {
    return (filters?.projectFilters ?? []).flatMap((project) => {
      if (project.id === undefined) {
        return []
      }

      return [{ id: project.id, label: project.name ?? "" }]
    })
  }

  return (filters?.userFilters ?? []).flatMap((user) => {
    if (user.id === undefined) {
      return []
    }

    const label = [user.firstName, user.lastName].filter(Boolean).join(" ")

    return [{ id: user.id, label }]
  })
}

export const getResponderOptions = ({
  surveyType,
  filters,
  responsesForStatus,
}: {
  surveyType?: SurveyType
  filters?: SurveyFilters
  responsesForStatus?: SurveyResponse[]
}): ViewOption[] =>
  getResponderBaseOptions(surveyType, filters).map((option) =>
    withStatus(
      option,
      responsesForStatus?.filter(
        (response) => getResponderId(response, surveyType) === option.id,
      ),
    ),
  )

export const getIterationOptions = ({
  iterationType,
  iterations,
  responsesOfResponder,
}: {
  iterationType?: IterationType
  iterations?: number[] | null
  responsesOfResponder?: SurveyResponse[]
}): ViewOption[] => {
  if (isNonRecurrent(iterationType)) {
    return []
  }

  const uniqueIterations = Array.from(new Set(iterations ?? [])).sort(
    (a, b) => a - b,
  )
  const iterationWord = getSurveyIterationType(iterationType)

  return uniqueIterations.map((iteration) =>
    withStatus(
      { id: iteration, label: `${iterationWord} ${iteration}` },
      responsesOfResponder?.filter(
        (response) => response.iterationNumber === iteration,
      ),
    ),
  )
}

export const findResponderResponse = ({
  responses,
  iteration,
  iterationType,
}: {
  responses?: SurveyResponse[] | null
  iteration?: number
  iterationType?: IterationType
}): SurveyResponse | undefined => {
  if (!responses) {
    return undefined
  }

  if (isNonRecurrent(iterationType)) {
    return preferSubmitted(responses)
  }

  if (iteration === undefined) {
    return undefined
  }

  return preferSubmitted(
    responses.filter((response) => response.iterationNumber === iteration),
  )
}

export const ALL_RESPONSES = 10000

export type ViewSearch = { responderId?: number; iteration?: number }

const toId = (value: unknown) => {
  const id = typeof value === "string" ? Number(value) : value

  if (typeof id !== "number" || !Number.isInteger(id) || id < 0) {
    return undefined
  }

  return id
}

export const parseViewSearch = (
  search: Record<string, unknown>,
): ViewSearch => {
  const parsed: ViewSearch = {}
  const responderId = toId(search.responderId)
  const iteration = toId(search.iteration)

  if (responderId !== undefined) {
    parsed.responderId = responderId
  }
  if (iteration !== undefined) {
    parsed.iteration = iteration
  }

  return parsed
}

export const getKnownSelection = ({
  surveyType,
  iterationType,
  filters,
  responderId,
  iteration,
}: {
  surveyType?: SurveyType
  iterationType?: IterationType
  filters?: SurveyFilters
  responderId?: number
  iteration?: number
}): ViewSearch => {
  if (!filters) {
    return {}
  }

  const hasResponder = getResponderBaseOptions(surveyType, filters).some(
    ({ id }) => id === responderId,
  )
  const hasIteration =
    !isNonRecurrent(iterationType) &&
    (filters.iterationFilters ?? []).some((known) => known === iteration)

  return toSelection(
    hasResponder ? responderId : undefined,
    hasIteration ? iteration : undefined,
  )
}

export const isAnyOf = (value: number) => ({
  type: ApiDynamicFilterType.IsAnyOf,
  values: [value],
})

export const getResponsesOfResponder = ({
  responses,
  surveyType,
  responderId,
}: {
  responses: SurveyResponse[]
  surveyType?: SurveyType
  responderId?: number
}) => {
  if (responderId === undefined) {
    return responses
  }

  return responses.filter(
    (response) => getResponderId(response, surveyType) === responderId,
  )
}

export const getReportFilters = ({
  iteration,
}: {
  iteration?: number
}): SurveyResponsesFilters => {
  const filters: SurveyResponsesFilters = {
    take: ALL_RESPONSES,
    includeAnswers: true,
    statusFilters: isAnyOf(SurveyStatus.Submitted),
  }

  if (iteration !== undefined) {
    filters.iterationFilters = isAnyOf(iteration)
  }

  return filters
}

export const getReportType = (
  mode: Exclude<ResponsesViewMode, "response">,
  iterationType?: IterationType,
): ReportType => {
  if (mode === "responderSummary") {
    return "iterationAggregateForIndividual"
  }
  if (mode === "iterationSummary") {
    return "respondersAggregate"
  }
  if (isNonRecurrent(iterationType)) {
    return "respondersAggregate"
  }

  return "allResponses"
}

export const getResponseViewSearch = (
  response: SurveyResponse,
  survey: { surveyType?: SurveyType; iterationType?: IterationType },
): ViewSearch => {
  const search: ViewSearch = {}
  const responderId = getResponderId(response, survey.surveyType)

  if (responderId !== undefined) {
    search.responderId = responderId
  }

  if (
    !isNonRecurrent(survey.iterationType) &&
    response.iterationNumber !== undefined
  ) {
    search.iteration = response.iterationNumber
  }

  return search
}

export const ALL_OPTION_ID = -1

export type ResponderQueueRow = {
  id: number
  name: string
  statusLabel?: string
  statusTone?: QueueStatusTone
  isActive: boolean
  selection: ViewSearch
}

export const toSelection = (responderId?: number, iteration?: number) => {
  const selection: ViewSearch = {}

  if (responderId !== undefined) {
    selection.responderId = responderId
  }
  if (iteration !== undefined) {
    selection.iteration = iteration
  }

  return selection
}

export const getStatusDisplay = (
  status: ResponseStatus,
): { statusLabel: string; statusTone: QueueStatusTone } => {
  if (status === "submitted") {
    return {
      statusLabel: t({
        id: "survey_response_submitted_status",
        message: "Submitted",
      }),
      statusTone: "success",
    }
  }

  if (status === "draft") {
    return {
      statusLabel: t({ id: "survey_response_draft_status", message: "Draft" }),
      statusTone: "orange",
    }
  }

  return { statusLabel: t`No response`, statusTone: "muted" }
}

const getRowStatusDisplay = (status?: ResponseStatus) => {
  if (status === undefined || status === "submitted") {
    return {}
  }

  return getStatusDisplay(status)
}

export const getSummaryQueueRow = ({
  responderId,
  iteration,
  label,
}: {
  responderId?: number
  iteration?: number
  label: string
}): ResponderQueueRow => ({
  id: ALL_OPTION_ID,
  name: label,
  isActive: responderId === undefined,
  selection: toSelection(undefined, iteration),
})

export const getResponderQueueRows = ({
  responderOptions,
  responderId,
  iteration,
}: {
  responderOptions: ViewOption[]
  responderId?: number
  iteration?: number
}): ResponderQueueRow[] =>
  responderOptions.map(({ id, label, status }) => ({
    id,
    name: label,
    ...getRowStatusDisplay(status),
    isActive: id === responderId,
    selection: toSelection(id, iteration),
  }))

export const getSubmittedCount = (responderOptions: ViewOption[]) => {
  if (!responderOptions.some(({ status }) => status !== undefined)) {
    return undefined
  }

  return {
    submitted: responderOptions.filter(({ status }) => status === "submitted")
      .length,
    total: responderOptions.length,
  }
}

export const getEmptyReportMessage = (
  mode: Exclude<ResponsesViewMode, "response">,
  {
    responderLabel,
    iterationLabel,
  }: { responderLabel: string; iterationLabel: string },
) => {
  if (mode === "iterationSummary") {
    return t`No submitted responses for ${iterationLabel} yet.`
  }

  if (mode === "responderSummary") {
    return t`${responderLabel} hasn't submitted any responses yet.`
  }

  return t`No submitted responses yet.`
}

export const getMissingResponseMessage = ({
  hasDraft,
  responderLabel,
  iterationLabel,
}: {
  hasDraft: boolean
  responderLabel: string
  iterationLabel?: string
}) => {
  if (!iterationLabel) {
    return hasDraft
      ? t`${responderLabel} hasn't submitted yet.`
      : t`${responderLabel} didn't answer.`
  }

  return hasDraft
    ? t`${responderLabel} hasn't submitted ${iterationLabel} yet.`
    : t`${responderLabel} didn't answer ${iterationLabel}.`
}
